/**
 * Reaction block body in Loro (spec §7.4.9). `coefficients`, `agents`, `conditions` and
 * `quantities` hold authored values only; everything derivable from `rxn` stays out of Loro (I3).
 * Entry maps are keyed by UUID and their values are plain and replaced whole on edit.
 */
import { LoroMap, LoroText } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";

export type AgentRole = "solvent" | "catalyst" | "reagent";

export interface AgentEntry {
  role: AgentRole;
  iri: string | null;
  label: string;
}
export interface ConditionEntry {
  kind: string;
  value: string;
  unit: string | null;
  note: string;
}
export interface QuantityEntry {
  kind: string;
  value: string;
  unit: string | null;
}

export type EntryMapKey = "agents" | "conditions" | "quantities";

export interface ReactionState {
  rxn: string;
  caption: string;
  reactionType: string | null;
  coefficients: Record<string, number>;
  agents: Array<AgentEntry & { id: string }>;
  conditions: Array<ConditionEntry & { id: string }>;
  quantities: Array<QuantityEntry & { id: string }>;
}

// the quantity vocabulary is shared with the block facts of §7.6.2
export { KM_ATMOSPHERE, KM_YIELD, QUANTITYKIND_NS, UNIT_NS, unitSymbol } from "../../lib/quantities";
/** ChEBI "chemical entity": narrows the species picker */
export const CHEMICAL_ENTITY = "http://purl.obolibrary.org/obo/CHEBI_24431";

function map(body: LoroMap, key: string): LoroMap {
  const m = body.get(key);
  return m instanceof LoroMap ? m : body.setContainer(key, new LoroMap());
}

function entries<T>(body: LoroMap, key: EntryMapKey): Array<T & { id: string }> {
  const m = body.get(key);
  if (!(m instanceof LoroMap)) return [];
  const out: Array<T & { id: string }> = [];
  for (const id of m.keys()) {
    const v = m.get(id);
    if (v && typeof v === "object" && !(v instanceof LoroMap)) out.push({ ...(v as T), id });
  }
  return out.sort((a, b) => a.id.localeCompare(b.id)); // uuidv7: creation order
}

export function readReaction(body: LoroMap): ReactionState {
  const coeff = body.get("coefficients");
  const coefficients: Record<string, number> = {};
  if (coeff instanceof LoroMap) {
    for (const k of coeff.keys()) {
      const v = coeff.get(k);
      if (typeof v === "number" && v > 0) coefficients[k] = v;
    }
  }
  const caption = body.get("caption");
  const rt = body.get("reactionType");
  return {
    rxn: String(body.get("rxn") ?? ""),
    caption: caption instanceof LoroText ? caption.toString() : "",
    reactionType: typeof rt === "string" && rt ? rt : null,
    coefficients,
    agents: entries<AgentEntry>(body, "agents"),
    conditions: entries<ConditionEntry>(body, "conditions"),
    quantities: entries<QuantityEntry>(body, "quantities"),
  };
}

export function sameReaction(a: ReactionState, b: ReactionState): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Set (value > 0) or clear (null) the coefficient override for a species. */
export function setCoefficient(body: LoroMap, smiles: string, value: number | null): void {
  const m = map(body, "coefficients");
  if (value === null || !(value > 0)) m.delete(smiles);
  else m.set(smiles, value);
}

/** §7.4.9 rule 3: drop overrides whose species is no longer drawn (called by the save path). */
export function pruneCoefficients(body: LoroMap, drawn: Set<string>): void {
  const m = body.get("coefficients");
  if (!(m instanceof LoroMap)) return;
  for (const k of m.keys()) if (!drawn.has(k)) m.delete(k);
}

export function putEntry(body: LoroMap, key: "agents", value: AgentEntry, id?: string): string;
export function putEntry(body: LoroMap, key: "conditions", value: ConditionEntry, id?: string): string;
export function putEntry(body: LoroMap, key: "quantities", value: QuantityEntry, id?: string): string;
export function putEntry(body: LoroMap, key: EntryMapKey, value: AgentEntry | ConditionEntry | QuantityEntry, id = uuidv7()): string {
  map(body, key).set(id, { ...value });
  return id;
}

export function removeEntry(body: LoroMap, key: EntryMapKey, id: string): void {
  map(body, key).delete(id);
}

export function initReactionBody(body: LoroMap): void {
  body.set("ket", "");
  body.set("rxn", "");
  body.setContainer("caption", new LoroText());
  body.set("reactionType", null);
  for (const key of ["coefficients", "agents", "conditions", "quantities"]) body.setContainer(key, new LoroMap());
}
